import zxcvbn from 'zxcvbn';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MIN_STRENGTH = 2; // zxcvbn score (0-4)

export interface PasswordValidationResult {
  valid: boolean;
  score?: number;
  feedback?: {
    warning?: string;
    suggestions?: string[];
  };
}

/**
 * Validates password strength using zxcvbn library
 * Matches backend validation requirements
 * 
 * @param password - The password to validate
 * @returns PasswordValidationResult with validation status and feedback
 */
export const validatePasswordStrength = (password: string): PasswordValidationResult => {
  // Length check
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      valid: false,
      score: undefined,
      feedback: {
        warning: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
        suggestions: []
      }
    };
  }

  // Strength check using zxcvbn
  const result = zxcvbn(password);
  
  if (result.score < PASSWORD_MIN_STRENGTH) {
    return {
      valid: false,
      score: result.score,
      feedback: result.feedback
    };
  }

  return {
    valid: true,
    score: result.score,
    feedback: result.feedback
  };
};

/**
 * Get password strength level description
 */
export const getPasswordStrengthLevel = (score: number | undefined): {
  label: string;
  color: string;
} => {
  if (score === undefined) {
    return { label: 'Too short', color: 'text-red-600' };
  }
  
  switch (score) {
    case 0:
    case 1:
      return { label: 'Weak', color: 'text-red-600' };
    case 2:
      return { label: 'Fair', color: 'text-yellow-600' };
    case 3:
      return { label: 'Good', color: 'text-blue-600' };
    case 4:
      return { label: 'Strong', color: 'text-green-600' };
    default:
      return { label: 'Unknown', color: 'text-gray-600' };
  }
};

/**
 * Get strength meter color classes for progress bar
 */
export const getStrengthMeterColor = (score: number | undefined): string => {
  if (score === undefined || score < 2) {
    return 'bg-red-500';
  }
  if (score === 2) {
    return 'bg-yellow-500';
  }
  if (score === 3) {
    return 'bg-blue-500';
  }
  return 'bg-green-500';
};

/**
 * Get strength meter width percentage
 */
export const getStrengthMeterWidth = (score: number | undefined): number => {
  if (score === undefined) {
    return 0;
  }
  return (score + 1) * 20; // 0->20%, 1->40%, 2->60%, 3->80%, 4->100%
};
